/**
 * CP2.2 — ShippingQuotePicker
 *
 * Renders quotes returned by CP2.1's public rate proxy, grouped into
 * four speed bands (Economy · Standard · Express · Overnight).
 *
 * Displays for each service: carrier badge, service name, estimated
 * delivery, and rate. Selection persists via the parent's callback.
 *
 * Guardrails (per CP2.2 acceptance criteria):
 *   * Never reveals carrier account #, warehouse internals, raw
 *     provider payload, or internal tenant IDs. Only
 *     carrier + service + rate + estimated_days are surfaced.
 *   * Shows loading / retry / no-rate / expired / invalidated /
 *     rate-limited / carrier-partial states with clear copy.
 *   * Keyboard-accessible radio group; screen-reader friendly.
 */
import React from "react";
import { Button } from "../../ui/button";
import { Loader2, RefreshCw, AlertCircle, PackageCheck } from "lucide-react";

const BAND_LABELS = {
  economy: { title: "Economy", subtitle: "5–7 business days" },
  standard: { title: "Standard", subtitle: "3 business days" },
  express: { title: "Express", subtitle: "2 business days" },
  overnight: { title: "Overnight", subtitle: "1 business day" },
};

const BAND_ORDER = ["economy", "standard", "express", "overnight"];

function formatCurrency(amount, currency = "USD") {
  const n = Number(amount || 0);
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency,
  }).format(n);
}

export default function ShippingQuotePicker({
  status,               // idle | loading | ready | expired | invalidated | error
  quotes,               // [{carrier, service_code, service_name, speed_band, rate_amount, currency, estimated_days}]
  partialProviders,     // e.g. ["usps:auth_failed"] — friendly-summarized below
  errorCode,
  onSelect,
  selectedServiceCode,
  onRetry,
}) {
  if (status === "loading") {
    return (
      <div
        className="flex items-center gap-2 text-sm text-stone-600 py-6"
        role="status" aria-live="polite"
        data-testid="ship-quotes-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Fetching shipping rates…
      </div>
    );
  }

  if (status === "error") {
    return (
      <ErrorState
        code={errorCode}
        onRetry={onRetry}
      />
    );
  }

  if (status === "expired" || status === "invalidated") {
    return (
      <div
        className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm"
        role="status" aria-live="polite"
        data-testid={`ship-quotes-${status}`}
      >
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-amber-900">
              {status === "expired"
                ? "Your shipping quote expired."
                : "Your cart or address changed."}
            </div>
            <div className="text-amber-800 mt-0.5">
              Please refresh rates to continue.
            </div>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={onRetry}
            data-testid="ship-quotes-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Refresh rates
          </Button>
        </div>
      </div>
    );
  }

  if (status !== "ready" || !Array.isArray(quotes) || quotes.length === 0) {
    return (
      <NoRatesState
        partialProviders={partialProviders}
        onRetry={onRetry}
      />
    );
  }

  // Group by speed band and preserve BAND_ORDER
  const grouped = BAND_ORDER
    .map((band) => ({
      band,
      title: BAND_LABELS[band].title,
      subtitle: BAND_LABELS[band].subtitle,
      items: quotes
        .filter((q) => q.speed_band === band)
        // Cheapest first within a band
        .sort((a, b) => Number(a.rate_amount) - Number(b.rate_amount)),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div
      className="space-y-4"
      role="radiogroup"
      aria-label="Shipping service"
      data-testid="ship-quotes-picker"
    >
      {grouped.map((group) => (
        <div key={group.band} data-testid={`ship-band-${group.band}`}>
          <div className="text-xs uppercase tracking-wide text-stone-500 font-medium mb-1.5">
            {group.title}
            <span className="text-stone-400 normal-case font-normal ml-1.5">
              · {group.subtitle}
            </span>
          </div>
          <div className="space-y-1.5">
            {group.items.map((q) => (
              <QuoteRow
                key={`${q.carrier}-${q.service_code}`}
                quote={q}
                selected={selectedServiceCode === q.service_code}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      ))}
      <PartialProvidersHint partialProviders={partialProviders} />
    </div>
  );
}

function QuoteRow({ quote, selected, onSelect }) {
  const rateText = formatCurrency(quote.rate_amount, quote.currency);
  const eta = quote.estimated_days
    ? `Est. ${quote.estimated_days} day${quote.estimated_days === 1 ? "" : "s"}`
    : "Estimated delivery unavailable";
  return (
    <label
      className={[
        "flex items-center gap-3 p-3 rounded-md border cursor-pointer transition",
        selected
          ? "border-orange-500 bg-orange-50"
          : "border-stone-200 bg-white hover:border-stone-400",
      ].join(" ")}
      data-testid={`ship-quote-${quote.service_code}`}
    >
      <input
        type="radio"
        name="ship-service"
        value={quote.service_code}
        checked={!!selected}
        onChange={() => onSelect && onSelect(quote)}
        className="accent-orange-500"
        aria-label={`${quote.service_name} for ${rateText}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-stone-900 truncate">
          {quote.service_name || quote.service_code}
        </div>
        <div className="text-xs text-stone-500">
          {(quote.carrier || "").toUpperCase()} · {eta}
        </div>
      </div>
      <div className="text-sm font-semibold text-stone-900 tabular-nums">
        {rateText}
      </div>
    </label>
  );
}

function ErrorState({ code, onRetry }) {
  const map = {
    cart_has_ineligible_products: {
      title: "Some items in your cart cannot be shipped.",
      hint: "Remove ineligible items or choose local pickup / delivery.",
    },
    no_parcel_eligible_products_in_cart: {
      title: "Ship to My Address is unavailable for this cart.",
      hint: "Your cart contains no shippable items.",
    },
    no_qualifying_package: {
      title: "Ship to My Address is unavailable for this cart.",
      hint: "The items exceed the largest available package. Try splitting the order or contact the merchant.",
    },
    missing_product_weight: {
      title: "Shipping is unavailable for one of the items.",
      hint: "Please contact the merchant — a product is missing shipping details.",
    },
    tenant_has_no_packaging: {
      title: "Shipping is not yet configured.",
      hint: "The merchant hasn't finished setting up packaging.",
    },
    no_carrier_accounts_configured: {
      title: "Shipping is not yet configured.",
      hint: "The merchant hasn't connected a carrier.",
    },
    invalid_address: {
      title: "Please check your shipping address.",
      hint: "Some required fields appear missing or invalid.",
    },
    rate_quote_failed: {
      title: "We couldn't fetch shipping rates.",
      hint: "Please try again in a moment.",
    },
    network_error: {
      title: "Network problem.",
      hint: "Please retry.",
    },
  };
  const c = map[code] || map.rate_quote_failed;
  const isRetryable = ["rate_quote_failed", "network_error"].includes(code);
  return (
    <div
      className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm"
      role="alert" aria-live="assertive"
      data-testid={`ship-quotes-error-${code || "generic"}`}
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-rose-900">{c.title}</div>
          <div className="text-rose-800 mt-0.5">{c.hint}</div>
        </div>
        {isRetryable && (
          <Button
            variant="outline" size="sm"
            onClick={onRetry}
            data-testid="ship-quotes-retry"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}

function NoRatesState({ partialProviders, onRetry }) {
  return (
    <div
      className="rounded-md border border-stone-200 bg-stone-50 p-3 text-sm"
      role="status" aria-live="polite"
      data-testid="ship-quotes-none"
    >
      <div className="flex items-start gap-2">
        <PackageCheck className="w-4 h-4 text-stone-500 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-stone-800">
            No shipping options available right now.
          </div>
          <div className="text-stone-600 mt-0.5">
            Please try a different address or contact the merchant.
          </div>
          <PartialProvidersHint partialProviders={partialProviders} />
        </div>
        <Button
          variant="outline" size="sm"
          onClick={onRetry}
          data-testid="ship-quotes-retry-none"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Retry
        </Button>
      </div>
    </div>
  );
}

function PartialProvidersHint({ partialProviders }) {
  if (!Array.isArray(partialProviders) || partialProviders.length === 0) return null;
  // NEVER surface raw carrier error strings — abstract to a count.
  const count = partialProviders.length;
  return (
    <p
      className="text-xs text-stone-500 mt-2"
      data-testid="ship-partial-providers"
    >
      {count} shipping partner{count === 1 ? "" : "s"} unavailable — only the
      partners returning rates are shown above.
    </p>
  );
}
